import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { showAPI } from '../utils/api';
import api from '../utils/api';
import { Printer, ArrowLeft, CheckCircle, Horse, ArrowsClockwise, Bed, Tent } from '@phosphor-icons/react';
import { toast } from 'sonner';

const EntryConfirmation = () => {
  const { showId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [show, setShow] = useState(null);
  const [entries, setEntries] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && showId) loadData();
  }, [user, showId]);

  const loadData = async () => {
    try {
      const { data: showData } = await showAPI.get(showId);
      setShow(showData);
      
      // Load all entries for this rider across all classes
      const allEntries = [];
      for (const cls of showData.classes) {
        try {
          const { data: classEntries } = await api.get(`/shows/${showId}/classes/${cls.id}/entries`);
          const myEntries = classEntries.filter(e => e.rider_name === user?.name || e.rider_id === user?.id);
          myEntries.forEach(e => allEntries.push({ ...e, class_name: cls.name, entry_fee: cls.entry_fee, class_id: cls.id }));
        } catch {}
      }
      setEntries(allEntries);

      // Load this rider's stall/RV reservations for this show so we can
      // echo exactly which spots they reserved on the confirmation.
      try {
        const { data: mySpots } = await api.get(`/shows/${showId}/my-spot-reservations`);
        setReservations(mySpots || []);
      } catch { /* non-fatal */ }
    } catch {
      toast.error('Failed to load confirmation');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#0096FF] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!show || (entries.length === 0 && reservations.length === 0)) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-white">
        <div className="text-center">
          <p className="text-[#9E9E9E] mb-4">No entries found for this show.</p>
          <button onClick={() => navigate('/shows')} className="text-[#0096FF] hover:underline">Browse Shows</button>
        </div>
      </div>
    );
  }

  const directEntries = entries.filter(e => !e.is_rollover);
  const rolloverEntries = entries.filter(e => e.is_rollover);
  const totalFees = entries.reduce((sum, e) => sum + parseFloat(e.entry_fee || 0), 0);
  // Name lookup so rollover entries can show "rolled in from Open Barrels"
  const classNameById = Object.fromEntries((show?.classes || []).map(c => [c.id, c.name]));
  const stallReservations = reservations.filter(r => r.type === 'stall');
  const rvReservations = reservations.filter(r => r.type === 'rv');
  const reservationTotal = reservations.reduce((sum, r) => sum + parseFloat(r.fee || 0), 0);
  const formatSpot = (r) => {
    const barn = r.barn && r.barn !== 'Main' ? `${r.barn}-` : '';
    const row = r.row ? `${r.row}` : '';
    return `${barn}${row}${r.spot_number ?? ''}`;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header - hide on print */}
        <div className="flex items-center justify-between mb-6 print:hidden">
          <button onClick={() => navigate('/shows')} className="flex items-center gap-2 text-[#9E9E9E] hover:text-white transition-colors">
            <ArrowLeft size={18} /> Back to Shows
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-[#0096FF]/15 text-[#0096FF] border border-[#0096FF]/30 rounded-lg text-sm font-semibold hover:bg-[#0096FF]/25 transition-colors"
            data-testid="print-confirmation-btn"
          >
            <Printer size={16} weight="bold" /> Print / Save
          </button>
        </div>

        {/* Confirmation Card */}
        <div className="bg-[#161616] border border-[#262626] rounded-xl p-8 print:bg-white print:text-black print:border-gray-300" data-testid="entry-confirmation-card">
          {/* Show Logo / Header */}
          <div className="text-center mb-6 pb-6 border-b border-[#262626] print:border-gray-300">
            <CheckCircle size={40} weight="fill" className="text-[#34C759] mx-auto mb-3 print:text-green-600" />
            <h1 className="text-2xl font-black mb-1 print:text-black" style={{fontFamily: 'Outfit'}}>Entry Confirmation</h1>
            <p className="text-[#9E9E9E] text-sm print:text-gray-600">The Show Master</p>
          </div>

          {/* Show Details */}
          <div className="mb-6 pb-4 border-b border-[#262626] print:border-gray-300">
            <h2 className="text-lg font-bold mb-2 print:text-black" style={{fontFamily: 'Outfit'}}>{show.name}</h2>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-[#9E9E9E] print:text-gray-500">Location:</span>
                <span className="text-white ml-2 print:text-black">{show.location}</span>
              </div>
              <div>
                <span className="text-[#9E9E9E] print:text-gray-500">Date:</span>
                <span className="text-white ml-2 print:text-black">{show.start_date}{show.end_date !== show.start_date ? ` - ${show.end_date}` : ''}</span>
              </div>
            </div>
          </div>

          {/* Rider Info */}
          <div className="mb-6 pb-4 border-b border-[#262626] print:border-gray-300">
            <h3 className="text-sm font-bold text-[#9E9E9E] uppercase tracking-wider mb-2 print:text-gray-500">Rider Information</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-[#9E9E9E] print:text-gray-500">Name:</span>
                <span className="text-white ml-2 font-semibold print:text-black">{entries[0]?.rider_name}</span>
              </div>
              {entries[0]?.horse_name && (
                <div>
                  <span className="text-[#9E9E9E] print:text-gray-500">Horse:</span>
                  <span className="text-white ml-2 font-semibold print:text-black">{entries[0]?.horse_name}</span>
                </div>
              )}
            </div>
          </div>

          {/* Classes Entered */}
          <div className="mb-6">
            <h3 className="text-sm font-bold text-[#9E9E9E] uppercase tracking-wider mb-3 print:text-gray-500">Classes Entered</h3>
            <div className="space-y-2">
              {directEntries.map((entry, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg print:bg-gray-100 print:border print:border-gray-200">
                  <div className="flex items-center gap-2">
                    <Horse size={14} weight="bold" className="text-[#0096FF] print:text-blue-600" />
                    <span className="text-white text-sm font-semibold print:text-black">{entry.class_name}</span>
                  </div>
                  <span className="text-[#34C759] font-bold text-sm print:text-green-600" style={{fontFamily: 'JetBrains Mono'}}>${entry.entry_fee}</span>
                </div>
              ))}
              {rolloverEntries.map((entry, i) => {
                const srcName = entry.rollover_source_class_id ? classNameById[entry.rollover_source_class_id] : null;
                return (
                  <div key={`ro-${i}`} className="flex items-center justify-between p-3 bg-[#FF9500]/5 rounded-lg border border-[#FF9500]/20 print:bg-orange-50 print:border-orange-200">
                    <div className="flex items-center gap-2 min-w-0">
                      <ArrowsClockwise size={14} weight="bold" className="text-[#FF9500] print:text-orange-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white text-sm font-semibold print:text-black">{entry.class_name}</span>
                          <span className="text-[#FF9500] text-[10px] font-bold print:text-orange-600">ROLLOVER</span>
                        </div>
                        {srcName && (
                          <p className="text-[#9E9E9E] text-[11px] mt-0.5 print:text-gray-600" data-testid={`rollover-source-${i}`}>
                            Rolls your time from <span className="text-[#FF9500] font-semibold print:text-orange-600">{srcName}</span>
                          </p>
                        )}
                      </div>
                    </div>
                    <span className="text-[#FF9500] font-bold text-sm print:text-orange-600" style={{fontFamily: 'JetBrains Mono'}}>${entry.entry_fee}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Stalls & RV Reservations */}
          {(stallReservations.length > 0 || rvReservations.length > 0) && (
            <div className="mb-6" data-testid="reservations-section">
              <h3 className="text-sm font-bold text-[#9E9E9E] uppercase tracking-wider mb-3 print:text-gray-500">Stalls & RV Reserved</h3>
              <div className="space-y-2">
                {stallReservations.map((r, i) => (
                  <div key={`st-${i}`} className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg print:bg-gray-100 print:border print:border-gray-200" data-testid={`stall-reservation-${i}`}>
                    <div className="flex items-center gap-2">
                      <Bed size={14} weight="bold" className="text-[#0096FF] print:text-blue-600" />
                      <span className="text-white text-sm font-semibold print:text-black">Stall {formatSpot(r)}</span>
                      {r.pricing_type && (
                        <span className="text-[#9E9E9E] text-[10px] uppercase tracking-wider print:text-gray-500">
                          {r.pricing_type === 'weekend' ? 'full show' : `${r.nights || ''} night${(r.nights || 0) !== 1 ? 's' : ''}`}
                        </span>
                      )}
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                        r.status === 'PAID'
                          ? 'bg-[#34C759]/15 text-[#34C759] print:bg-green-100 print:text-green-700'
                          : 'bg-[#FF9500]/15 text-[#FF9500] print:bg-orange-100 print:text-orange-700'
                      }`}>{r.status}</span>
                    </div>
                    <span className="text-[#34C759] font-bold text-sm print:text-green-600" style={{fontFamily: 'JetBrains Mono'}}>${r.fee}</span>
                  </div>
                ))}
                {rvReservations.map((r, i) => (
                  <div key={`rv-${i}`} className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg print:bg-gray-100 print:border print:border-gray-200" data-testid={`rv-reservation-${i}`}>
                    <div className="flex items-center gap-2">
                      <Tent size={14} weight="bold" className="text-[#FACC15] print:text-yellow-600" />
                      <span className="text-white text-sm font-semibold print:text-black">RV {formatSpot(r)}</span>
                      {r.pricing_type && (
                        <span className="text-[#9E9E9E] text-[10px] uppercase tracking-wider print:text-gray-500">
                          {r.pricing_type === 'weekend' ? 'full show' : `${r.nights || ''} night${(r.nights || 0) !== 1 ? 's' : ''}`}
                        </span>
                      )}
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                        r.status === 'PAID'
                          ? 'bg-[#34C759]/15 text-[#34C759] print:bg-green-100 print:text-green-700'
                          : 'bg-[#FF9500]/15 text-[#FF9500] print:bg-orange-100 print:text-orange-700'
                      }`}>{r.status}</span>
                    </div>
                    <span className="text-[#34C759] font-bold text-sm print:text-green-600" style={{fontFamily: 'JetBrains Mono'}}>${r.fee}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Total */}
          <div className="p-4 bg-[#34C759]/10 rounded-lg border border-[#34C759]/30 print:bg-green-50 print:border-green-300">
            <div className="flex items-center justify-between">
              <span className="text-white font-bold print:text-black">
                {reservations.length > 0 ? 'Entries + Reservations Total' : 'Entry Fees Total'}
              </span>
              <span className="text-[#34C759] font-black text-xl print:text-green-600" style={{fontFamily: 'JetBrains Mono'}}>
                ${(totalFees + reservationTotal).toFixed(2)}
              </span>
            </div>
            {reservations.length > 0 && (
              <div className="mt-2 pt-2 border-t border-[#34C759]/20 flex items-center justify-between text-[11px] text-[#9E9E9E] print:text-gray-600">
                <span>Entries: ${totalFees.toFixed(2)}</span>
                <span>Stalls & RV: ${reservationTotal.toFixed(2)}</span>
              </div>
            )}
            <p className="text-[#9E9E9E] text-xs mt-1 print:text-gray-500">+ service fee + payment processing at checkout</p>
          </div>

          {/* Payment Status */}
          <div className="mt-4 text-center">
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${
              entries[0]?.payment_status === 'PAID' 
                ? 'bg-[#34C759]/15 text-[#34C759] print:bg-green-100 print:text-green-700' 
                : 'bg-[#FF9500]/15 text-[#FF9500] print:bg-orange-100 print:text-orange-700'
            }`}>
              Payment: {entries[0]?.payment_status || 'PENDING'}
            </span>
          </div>

          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-[#262626] text-center print:border-gray-300">
            <p className="text-[#555] text-xs print:text-gray-400">
              Confirmation generated {new Date().toLocaleString()} — The Show Master
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EntryConfirmation;
